import { ArrowLeft, CloudSync, History, Share2, ShieldCheck, Upload, Zap } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useConnectors } from "@noa/api-client";
import { ConformitSyncPanel } from "~/components/admin/conformit-sync-panel";
import { CustomJobPanel } from "~/components/admin/custom-job-panel";
import { GoogleDriveIcon } from "~/components/admin/google-drive-icon";
import { GoogleDriveSyncPanel } from "~/components/admin/google-drive-sync-panel";
import { MaximoSyncPanel } from "~/components/admin/maximo-sync-panel";
import { SharePointSyncPanel } from "~/components/admin/sharepoint-sync-panel";
import { UploadJobHistory } from "~/components/admin/upload-job-history";
import { UploadPanel } from "~/components/admin/upload-panel";
import { Screen } from "~/screens/screen";
import type { SharePointSyncForm } from "~/utils/sharepoint-sync-form";
import { Button, Card, Tabs, Title } from "~/components/ui";

export function UploadAdminScreen() {
  const { t } = useTranslation("admin");
  const [activeTab, setActiveTab] = useState("upload");
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  // Connector tabs stay hidden until the backend confirms they are configured,
  // so an unconfigured one never flashes in and out while this loads.
  const { connectors } = useConnectors();
  // Each relaunch remounts the SharePoint form with the settings of the sync to run again.
  const [sharePointForm, setSharePointForm] = useState<{
    key: number;
    values: SharePointSyncForm;
    fromJobId: string;
  } | null>(null);

  const handleViewJob = (jobId: string) => {
    setActiveJobId(jobId);
    setActiveTab("upload");
  };

  const handleNavigateToHistory = () => {
    setActiveTab("history");
  };

  const handleRelaunchSync = (values: SharePointSyncForm, fromJobId: string) => {
    setSharePointForm((previous) => ({ key: (previous?.key ?? 0) + 1, values, fromJobId }));
    setActiveTab("sharepoint");
  };

  const tabItems = [
    {
      key: "upload",
      label: (
        <span>
          <Upload size={14} /> {t("uploadAdmin.tabs.fileUpload")}
        </span>
      ),
      children: (
        <UploadPanel
          activeJobId={activeJobId}
          onActiveJobIdChange={setActiveJobId}
          // Running a sync again opens the SharePoint tab, so only while that tab is there.
          onRelaunchSync={connectors?.sharepoint ? handleRelaunchSync : undefined}
        />
      ),
    },
    connectors?.maximo && {
      key: "maximo",
      label: (
        <span>
          <CloudSync size={14} /> {t("uploadAdmin.tabs.maximoSync")}
        </span>
      ),
      children: <MaximoSyncPanel onNavigateToHistory={handleNavigateToHistory} />,
    },
    connectors?.sharepoint && {
      key: "sharepoint",
      label: (
        <span>
          <Share2 size={14} /> {t("uploadAdmin.tabs.sharePointSync")}
        </span>
      ),
      children: (
        <SharePointSyncPanel
          key={sharePointForm?.key}
          initialValues={sharePointForm?.values}
          relaunchedFrom={sharePointForm?.fromJobId}
          onNavigateToHistory={handleNavigateToHistory}
        />
      ),
    },
    connectors?.google_drive && {
      key: "google-drive",
      label: (
        <span>
          <GoogleDriveIcon size={14} /> {t("uploadAdmin.tabs.googleDriveSync")}
        </span>
      ),
      children: <GoogleDriveSyncPanel onNavigateToHistory={handleNavigateToHistory} />,
    },
    connectors?.conformit && {
      key: "conformit",
      label: (
        <span>
          <ShieldCheck size={14} /> {t("uploadAdmin.tabs.conformitSync")}
        </span>
      ),
      children: <ConformitSyncPanel onNavigateToHistory={handleNavigateToHistory} />,
    },
    {
      key: "custom",
      label: (
        <span>
          <Zap size={14} /> {t("uploadAdmin.tabs.customJob")}
        </span>
      ),
      children: <CustomJobPanel onNavigateToHistory={handleNavigateToHistory} />,
    },
    {
      key: "history",
      label: (
        <span>
          <History size={14} /> {t("uploadAdmin.tabs.jobHistory")}
        </span>
      ),
      children: (
        <Card size="small">
          <UploadJobHistory onViewJob={handleViewJob} hideTitle onNavigateToUpload={() => setActiveTab("upload")} />
        </Card>
      ),
    },
  ].filter((item) => !!item);

  return (
    <Screen>
      <div className="flex h-full w-full flex-col overflow-auto p-4 sm:p-6">
        <div className="mb-4 flex items-center gap-2">
          <Link to="/admin/schemas">
            <Button
              variant="text"
              icon={<ArrowLeft size={14} />}
              aria-label={t("common:back")}
              data-testid="back-button"
            />
          </Link>
          <Title level={3} className="m-0">
            {t("uploadAdmin.title")}
          </Title>
        </div>

        <div className="flex-1 rounded-lg p-4 bg-noa">
          <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="large" />
        </div>
      </div>
    </Screen>
  );
}
